import type { StudyMissionSelectDto, StudySelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import {
  useGetMeiaMissionThreads,
  useGetMeiaStudyMissionThreads,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaStudyThreadTaskReadiness,
} from '@queries/meia.query';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MeiaMissionScopeStrip } from './meia-mission-scope-strip.component';
import { MeiaP2AdvanceBar } from './meia-p2-advance-bar.component';
import { MeiaThreadCard } from './meia-thread-card.component';
import { MeiaThreadManager } from './meia-thread-manager.component';

type MeiaP2AuthoringProps = {
  canEdit: boolean;
  isMissionsLoading?: boolean;
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
  studyId: string;
};

export const MeiaP2Authoring: FC<MeiaP2AuthoringProps> = (props) => {
  const { canEdit, isMissionsLoading, missions, study, studyId } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedAction = searchParams.get('action');
  const requestedMissionId = searchParams.get('missionId');
  const requestedThreadId = searchParams.get('threadId');

  const validMissionId =
    requestedMissionId && missions.some((mission) => mission.id === requestedMissionId)
      ? requestedMissionId
      : missions[0]?.id;

  useEffect(() => {
    if (!validMissionId) return;
    if (requestedMissionId === validMissionId) return;
    const next = new URLSearchParams(searchParams);
    next.set('missionId', validMissionId);
    setSearchParams(next, { preventScrollReset: true, replace: true });
  }, [requestedMissionId, searchParams, setSearchParams, validMissionId]);

  const selectedMission = missions.find((mission) => mission.id === validMissionId);
  const { threads: allStudyThreads } = useGetMeiaStudyMissionThreads({ missions, studyId });
  const { data: missionThreads = [], isLoading: isThreadsLoading } = useGetMeiaMissionThreads({
    missionId: selectedMission?.id,
    studyId,
  });
  const { swimlanes } = useGetMeiaStudyThreadSwimlanes({ studyId, threads: missionThreads });
  const { taskReadiness } = useGetMeiaStudyThreadTaskReadiness({ studyId, threads: missionThreads });

  const taskRows = useMemo(
    () => taskReadiness.map((row) => ({ swimlaneId: row.swimlaneId, threadId: row.threadId })),
    [taskReadiness],
  );

  const selectedThread = missionThreads.find((thread) => thread.id === requestedThreadId) ?? missionThreads[0] ?? null;

  const handleSelectMission = (missionId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('threadId');
    next.set('missionId', missionId);
    setSearchParams(next, { preventScrollReset: true, replace: false });
  };

  const handleSelectThread = (threadId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('threadId', threadId);
    setSearchParams(next, { preventScrollReset: true, replace: false });
  };

  const handleCreateThreadActionConsumed = useCallback((): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { preventScrollReset: true, replace: true });
  }, [searchParams, setSearchParams]);

  const handleAddMissionInP1 = (): void => {
    navigate(meiaRoute.studyTab({ query: { action: 'addMission' }, studyId, tab: 'p1' }));
  };

  if (!isMissionsLoading && missions.length === 0) {
    return (
      <EmptyStateContainer
        className="min-h-[520px] rounded-xl bg-white shadow-sm"
        message={canEdit ? 'Add Mission in P1' : undefined}
        onClick={canEdit ? handleAddMissionInP1 : undefined}
        title="No Missions"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <BlueLabel
          className="text-xs"
          text="Phase 2 — Thread Design"
        />
        <h2 className="mt-1 text-lg font-semibold text-gray-950">Compose mission threads & define stage structure</h2>
        <p className="mt-1 text-xs text-gray-500">
          Gate G2 checks that every thread has a stage structure. Pick a thread to edit its pattern and stages below.
        </p>
      </div>

      <MeiaMissionScopeStrip
        headerButton={canEdit ? { onClick: handleAddMissionInP1, text: '+ Add Mission in P1' } : undefined}
        missions={missions}
        onSelectMission={handleSelectMission}
        phase="p2"
        selectedMissionId={selectedMission?.id}
        study={study}
        threads={allStudyThreads}
      />

      {selectedMission && (
        <MeiaThreadManager
          canEdit={canEdit}
          isLoading={isThreadsLoading}
          missionId={selectedMission.id}
          onCreateThreadActionConsumed={handleCreateThreadActionConsumed}
          onSelectThread={handleSelectThread}
          selectedThreadId={selectedThread?.id}
          shouldOpenCreateThread={requestedAction === 'addThread'}
          studyId={studyId}
          threads={missionThreads}
        />
      )}

      {selectedMission && selectedThread && (
        <MeiaThreadCard
          canEdit={canEdit}
          missionId={selectedMission.id}
          studyId={studyId}
          thread={selectedThread}
        />
      )}

      {selectedMission && (
        <MeiaP2AdvanceBar
          mission={selectedMission}
          studyId={studyId}
          swimlanes={swimlanes}
          taskRows={taskRows}
          threads={missionThreads}
        />
      )}
    </div>
  );
};
